import { useSyncExternalStore } from "react";

/**
 * «Yazıyor…», outside React state.
 *
 * TYPING IS THE ONE FRAME THAT INVALIDATES NOTHING: there is no endpoint behind it — the server
 * writes nothing and publishes one event — so the indicator IS the frame, held for a few seconds
 * and then forgotten.
 *
 * WHY A STORE AND NOT STATE IN A HOOK. The bridge that receives the frames is mounted once in the
 * shell, and the surface that DRAWS them (the panel, in the dock or on `/chat`) is somewhere else
 * entirely. Passing the names down would mean a context whose value changes on every keystroke of
 * every conversation — re-rendering the whole shell for a caption. `useSyncExternalStore` lets the
 * one component that shows them subscribe, and nothing else re-renders at all.
 *
 * The snapshot is CACHED per conversation and only replaced when the ids actually change, because
 * `useSyncExternalStore` compares with `Object.is`: a fresh array every call is an infinite render
 * loop, which is the mistake this comment exists to prevent.
 */

/** How long one `chat.typing` frame keeps a name in the indicator. */
export const TYPING_TTL_MS = 4_000;

const EMPTY: readonly string[] = [];

interface Entry {
  /** userId → the instant their name lapses. */
  readonly typists: Map<string, number>;
  /** The array handed to React, replaced only when the membership changes. */
  snapshot: readonly string[];
}

const conversations = new Map<string, Entry>();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function notify(): void {
  for (const listener of listeners) listener();
}

function entryFor(conversationId: string): Entry {
  const existing = conversations.get(conversationId);
  if (existing) return existing;
  const created: Entry = { typists: new Map(), snapshot: EMPTY };
  conversations.set(conversationId, created);
  return created;
}

function rebuild(entry: Entry, now: number): boolean {
  for (const [userId, expiry] of entry.typists) {
    if (expiry <= now) entry.typists.delete(userId);
  }
  const next = [...entry.typists.keys()];
  const same =
    next.length === entry.snapshot.length && next.every((id, index) => entry.snapshot[index] === id);
  if (same) return false;
  entry.snapshot = next.length === 0 ? EMPTY : next;
  return true;
}

/** One sweep for every conversation rather than a timer per typist. */
function ensureSweep(): void {
  if (timer || typeof window === "undefined") return;
  timer = setInterval(() => {
    const now = Date.now();
    let changed = false;
    for (const [conversationId, entry] of conversations) {
      if (rebuild(entry, now)) changed = true;
      if (entry.typists.size === 0 && entry.snapshot === EMPTY) conversations.delete(conversationId);
    }
    if (conversations.size === 0 && timer) {
      clearInterval(timer);
      timer = undefined;
    }
    if (changed) notify();
  }, 1_000);
  // Never keep a test runner (or a page) alive for a caption.
  (timer as unknown as { unref?: () => void }).unref?.();
}

export const typingStore = {
  /** A `chat.typing` frame arrived. The reader's own keystrokes are dropped by the bridge. */
  note(conversationId: string, userId: string, now = Date.now()): void {
    const entry = entryFor(conversationId);
    entry.typists.set(userId, now + TYPING_TTL_MS);
    ensureSweep();
    if (rebuild(entry, now)) notify();
  },

  /** A message from that person ends their «yazıyor» at once: it has arrived. */
  clear(conversationId: string, userId: string, now = Date.now()): void {
    const entry = conversations.get(conversationId);
    if (!entry?.typists.delete(userId)) return;
    if (rebuild(entry, now)) notify();
  },

  snapshot(conversationId: string | null): readonly string[] {
    if (!conversationId) return EMPTY;
    return conversations.get(conversationId)?.snapshot ?? EMPTY;
  },

  /**
   * An arrow property, not a method: `useSyncExternalStore` is handed this function on its own and
   * a method separated from its object is a lint error waiting to become a `this` bug.
   */
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Test seam. */
  reset(): void {
    conversations.clear();
    if (timer) clearInterval(timer);
    timer = undefined;
  },
};

/** Who is typing in one conversation, excluding the reader. Re-renders only its own subscriber. */
export function useTypingUserIds(conversationId: string | null): readonly string[] {
  return useSyncExternalStore(
    typingStore.subscribe,
    () => typingStore.snapshot(conversationId),
    () => EMPTY,
  );
}
